// NOTE: the three hero bodies state the service count in words (Thirteen /
// Treze / Trece). Adding or removing a service means updating all
// three hero bodies too.
// The services catalogue. Structure follows the Porter proposal anatomy the
// founder approved: every service names what it is, how long it runs, what the
// client leaves with, and what usually follows. Prices live in proposals.

const servicesCatalogContent = {
  'en-US': {
    seo: {
      title: 'Services | Lean it 101',
      description:
        'Strategy, transformation, software delivery and AI engineering services delivered by senior practitioners: from executive workshops to forward deployed engineering pods.',
    },
    hero: {
      kicker: 'The catalogue',
      headline: 'Know exactly what you are buying.',
      body: 'Thirteen services in six lines of work. Each one names its duration, its deliverable and its next step. Pick where it hurts and read the spec.',
      cta: 'Start a conversation',
    },
    proof: {
      numbers: [
        { value: 'R$50M+', label: 'documented annual savings' },
        { value: '80%', label: 'support tickets reduced' },
        { value: '100+', label: 'transformation initiatives' },
        { value: '3,000+', label: 'people trained' },
      ],
      names: 'Delivered inside Vale, Softplan, Ambev, Segware and Porter Group.',
    },
    meta: { duration: 'Duration', leave: 'You leave with', next: 'Usually followed by', phases: 'The phases', talk: 'Open a card about this' },
    groups: [
      {
        id: 'strategy',
        accent: 19,
        kicker: 'Strategy',
        cta: 'See the full strategic planning offer',
        ctaHref: '/services/strategic-planning',
        title: 'Strategy that reaches Monday morning',
        intro: 'Hoshin Kanri applied for real: objectives become indicators, indicators become initiatives, and the whole company can see the connection.',
        items: [
          {
            name: 'Strategic Planning and Deployment',
            body: 'One program under Hoshin Kanri. Leadership sets the direction in an executive workshop, the objectives travel the organization in catchball rounds until every area has negotiated its targets, in the open, and a monthly mentoring rhythm keeps the strategy alive through the year.',
            duration: '8 weeks, then a 12 month rhythm',
            leave: 'X-Matrix, strategy scorecard, deployment playbook and a leadership team that runs its own reviews',
            next: 'The next annual cycle, run by your team',
            phases: [
              { name: 'Executive Strategy Workshop', duration: '2 days' },
              { name: 'Catchball and Deployment', duration: 'About 8 weeks' },
              { name: 'Strategic Execution Mentoring', duration: '12 months' },
            ],
          },
        ],
      },
      {
        id: 'workshops',
        accent: 10,
        kicker: 'Facilitation',
        title: 'Workshops that end with decisions',
        intro: 'Focused rooms with the right people, facilitated to a concrete artifact instead of a photo of sticky notes.',
        items: [
          {
            name: 'Value Stream Mapping',
            body: 'The whole flow from request to delivery on one wall, with the waits, loops and handoffs priced in days.',
            duration: '2 to 3 days',
            leave: 'Current and future state maps with an improvement backlog',
            next: 'A redesign sprint or an FDE engagement',
          },
          {
            name: 'Product and Process Redesign',
            body: 'The Porter format: map, identify losses, define the new flow, and agree who does what between areas.',
            duration: '1 to 2 weeks across sessions',
            leave: 'The new flow, documented and agreed by the people who run it',
            next: 'Implementation support with visual management',
          },
          {
            name: 'Lean Foundation',
            body: 'Your leaders learn the foundation the Lean Pyramid stands on: value, flow, pull, waste, and improvement as daily work.',
            duration: '2 days',
            leave: 'A shared language for the transformation ahead',
            next: 'Software delivery and AI transformation',
          },
          {
            name: 'AI-Driven Business Design',
            body: 'Where AI genuinely changes your operation and where it just adds cost, decided on your processes and your numbers.',
            duration: '2 days',
            leave: 'A prioritized map of AI opportunities, each with effort sized in ranges',
            next: 'AI discovery and technical assessment',
          },
        ],
      },
      {
        id: 'sdlc',
        accent: 1,
        kicker: 'Software delivery and AI',
        title: 'Software delivery rebuilt for the agent era',
        intro: 'The Lean Pyramid 6.0 applied to how your teams build: humans own strategy and craft, agents do the heavy lifting, and the whole flow stays visible.',
        items: [
          {
            name: 'Software Delivery and AI Transformation',
            body: 'One transformation of your software development lifecycle (SDLC), run inside your delivery flow. Your engineering leadership writes the operating model, agents get wired into your repos with governance, senior engineers restructure what the new flow runs on, and everything is consolidated into a living playbook with a monthly coaching rhythm while the habits harden.',
            duration: 'Typically 8 to 12 weeks, scoped per system, then a monthly rhythm',
            leave: 'Agents producing verified work in your repos, the playbook versioned with your code, leaders who correct the system',
            next: 'An FDE pod, or executive advisory as the scope grows',
            phases: [
              { name: 'Foundation Workshop', duration: '2 days' },
              { name: 'Agent Orchestration', duration: '2 to 4 weeks' },
              { name: 'Architecture and Engineering', duration: 'Scoped per system' },
              { name: 'AI Delivery Playbook', duration: '1 to 2 weeks' },
              { name: 'Leadership Coaching', duration: 'Monthly' },
            ],
          },
        ],
      },
      {
        id: 'discovery',
        accent: 10,
        kicker: 'Discovery and assessment',
        title: 'Know before you build',
        intro: 'Short, sharp engagements that turn a vague suspicion into a decision with numbers attached.',
        items: [
          {
            name: 'Legacy Systems Assessment',
            body: 'What that old system really costs, what it would take to modernize, and in which order the risk comes out.',
            duration: '2 to 3 weeks',
            leave: 'A modernization map with effort, risk and sequence',
            next: 'An FDE pod to execute it',
          },
          {
            name: 'AI Process Improvement Discovery',
            body: 'We sit inside your operation, find the processes where AI pays for itself, and prove the best one with a working probe.',
            duration: '2 to 4 weeks',
            leave: 'A ranked opportunity list and one probe running on real work',
            next: 'Delivery, with your team or ours',
          },
        ],
      },
      {
        id: 'delivery',
        accent: 1,
        kicker: 'Delivery',
        title: 'Forward Deployed Engineering',
        intro: 'Senior engineers embed in your operation and take the problem from discovery to production. Agentic hybrid pods: one to three seniors directing a team of agents, governed end to end.',
        items: [
          {
            name: 'Agentic Hybrid Pod',
            body: 'The pod owns the outcome: learns the business, designs the solution, ships to production and stays accountable through go-live.',
            duration: 'Engagements from 6 weeks',
            leave: 'Working software in production and full delivery visibility',
            next: 'Handover to your team, or a retainer',
          },
          {
            name: 'Handover',
            body: 'Your team inherits the system with the standards, the vault and the operating knowledge, verified by working sessions rather than documents alone.',
            duration: '2 to 4 weeks',
            leave: 'A team that owns what was built',
            next: 'Optional advisory cadence',
          },
        ],
        cta: 'See how FDE works',
      },
      {
        id: 'recurring',
        accent: 19,
        kicker: 'Recurring',
        title: 'Stay sharp between engagements',
        intro: 'Continuity offers for leaders who want the outside eye to remain on the system.',
        items: [
          {
            name: 'Executive Advisory',
            body: 'A standing monthly session with your executive team on strategy, technology and organization.',
            duration: 'Monthly',
            leave: 'Decisions stress-tested before they get expensive',
            next: '',
          },
          {
            name: 'Board Counsel',
            body: 'Certified board-level counsel for technology and transformation questions.',
            duration: 'Per cycle',
            leave: 'A technology voice in the boardroom',
            next: '',
          },
          {
            name: 'FDE Retainer',
            body: 'The pod that built your system stays available for evolution and support.',
            duration: 'Monthly',
            leave: 'Production capability that keeps improving',
            next: '',
          },
        ],
      },
    ],
    footerCta: {
      title: 'Not sure which one fits?',
      body: 'Describe where the pain is and we answer with the shape we would propose, usually within one business day.',
      cta: 'Talk to us',
    },
  },

  'pt-BR': {
    seo: {
      title: 'Serviços | Lean it 101',
      description:
        'Estratégia, transformação, entrega de software e engenharia com IA entregues por praticantes seniores: do workshop executivo aos pods de forward deployed engineering.',
    },
    hero: {
      kicker: 'O catálogo',
      headline: 'Você sabe exatamente o que está contratando.',
      body: 'Treze serviços em seis frentes de trabalho. Cada um diz sua duração, sua entrega e seu próximo passo. Escolha onde dói e leia a ficha.',
      cta: 'Começar uma conversa',
    },
    proof: {
      numbers: [
        { value: 'R$50M+', label: 'em economia anual documentada' },
        { value: '80%', label: 'de redução em tickets de suporte' },
        { value: '100+', label: 'iniciativas de transformação' },
        { value: '3.000+', label: 'pessoas treinadas' },
      ],
      names: 'Entregue dentro de Vale, Softplan, Ambev, Segware e Porter Group.',
    },
    meta: { duration: 'Duração', leave: 'Você sai com', next: 'Normalmente seguido de', phases: 'As fases', talk: 'Abrir um cartão sobre isso' },
    groups: [
      {
        id: 'strategy',
        accent: 19,
        kicker: 'Estratégia',
        cta: 'Ver a oferta completa de planejamento estratégico',
        ctaHref: '/services/strategic-planning',
        title: 'Estratégia que chega na segunda-feira',
        intro: 'Hoshin Kanri aplicado de verdade: objetivos viram indicadores, indicadores viram iniciativas, e a empresa inteira enxerga a conexão.',
        items: [
          {
            name: 'Planejamento Estratégico e Desdobramento',
            body: 'Um programa sob Hoshin Kanri. A liderança define a direção em um workshop executivo, os objetivos percorrem a organização em rodadas de catchball até cada área ter negociado suas metas, às claras, e uma mentoria mensal mantém a estratégia viva ao longo do ano.',
            duration: '8 semanas, depois ritmo de 12 meses',
            leave: 'X-Matrix, strategy scorecard, playbook de desdobramento e uma liderança que conduz seus próprios reviews',
            next: 'O próximo ciclo anual, tocado pelo seu time',
            phases: [
              { name: 'Workshop Executivo de Estratégia', duration: '2 dias' },
              { name: 'Catchball e Desdobramento', duration: 'Cerca de 8 semanas' },
              { name: 'Mentoria de Execução Estratégica', duration: '12 meses' },
            ],
          },
        ],
      },
      {
        id: 'workshops',
        accent: 10,
        kicker: 'Facilitação',
        title: 'Workshops que terminam em decisão',
        intro: 'Salas focadas com as pessoas certas, facilitadas até um artefato concreto em vez de uma foto de post-its.',
        items: [
          {
            name: 'Value Stream Mapping',
            body: 'O fluxo inteiro do pedido à entrega em uma parede, com esperas, retrabalhos e handoffs precificados em dias.',
            duration: '2 a 3 dias',
            leave: 'Mapas de estado atual e futuro com backlog de melhorias',
            next: 'Um sprint de redesenho ou um engajamento FDE',
          },
          {
            name: 'Redesenho de Produto e Processo',
            body: 'O formato Porter: mapear, identificar perdas, definir o novo fluxo e acordar quem faz o quê entre áreas.',
            duration: '1 a 2 semanas em sessões',
            leave: 'O novo fluxo, documentado e acordado por quem opera',
            next: 'Apoio à implementação com gestão visual',
          },
          {
            name: 'Fundamentos Lean',
            body: 'Seus líderes aprendem a base sobre a qual a Pirâmide Lean se sustenta: valor, fluxo, puxada, desperdício e melhoria como trabalho diário.',
            duration: '2 dias',
            leave: 'Uma linguagem comum para a transformação que vem',
            next: 'Transformação da entrega de software com IA',
          },
          {
            name: 'Desenho de Negócio com IA',
            body: 'Onde a IA muda sua operação de verdade e onde ela só adiciona custo, decidido sobre seus processos e seus números.',
            duration: '2 dias',
            leave: 'Um mapa priorizado de oportunidades de IA, cada uma com esforço em faixas',
            next: 'Discovery e assessment técnico de IA',
          },
        ],
      },
      {
        id: 'sdlc',
        accent: 1,
        kicker: 'Entrega de software e IA',
        title: 'Entrega de software reconstruída para a era dos agentes',
        intro: 'A Lean Pyramid 6.0 aplicada ao jeito como seus times constroem: humanos donos da estratégia e do ofício, agentes fazendo o trabalho pesado, e o fluxo inteiro visível.',
        items: [
          {
            name: 'Transformação da Entrega de Software com IA',
            body: 'Uma transformação só do seu ciclo de desenvolvimento de software (SDLC), conduzida dentro do seu fluxo de entrega. Sua liderança de engenharia escreve o modelo operacional, os agentes entram nos seus repositórios com governança, engenheiros seniores reestruturam a base onde o novo fluxo roda, e tudo se consolida em um playbook vivo com um ritmo mensal de coaching enquanto os novos hábitos endurecem.',
            duration: 'Em geral de 8 a 12 semanas, dimensionado por sistema, depois ritmo mensal',
            leave: 'Agentes produzindo trabalho verificado nos seus repositórios, o playbook versionado com seu código, líderes que corrigem o sistema',
            next: 'Um pod de FDE, ou advisory executivo quando o escopo cresce',
            phases: [
              { name: 'Workshop de Fundamentos', duration: '2 dias' },
              { name: 'Orquestração de Agentes', duration: '2 a 4 semanas' },
              { name: 'Arquitetura e Engenharia', duration: 'Por sistema' },
              { name: 'Playbook de Entrega com IA', duration: '1 a 2 semanas' },
              { name: 'Coaching de Liderança', duration: 'Mensal' },
            ],
          },
        ],
      },
      {
        id: 'discovery',
        accent: 10,
        kicker: 'Discovery e assessment',
        title: 'Saiba antes de construir',
        intro: 'Engajamentos curtos e afiados que transformam uma suspeita vaga em decisão com números.',
        items: [
          {
            name: 'Assessment de Sistemas Legados',
            body: 'Quanto aquele sistema antigo custa de verdade, o que custaria modernizar e em que ordem o risco sai.',
            duration: '2 a 3 semanas',
            leave: 'Um mapa de modernização com esforço, risco e sequência',
            next: 'Um pod FDE para executar',
          },
          {
            name: 'Discovery de Melhoria de Processos com IA',
            body: 'Sentamos dentro da sua operação, achamos os processos onde IA se paga e provamos o melhor com uma sonda funcionando.',
            duration: '2 a 4 semanas',
            leave: 'Uma lista ranqueada de oportunidades e uma sonda rodando em trabalho real',
            next: 'Entrega, com seu time ou o nosso',
          },
        ],
      },
      {
        id: 'delivery',
        accent: 1,
        kicker: 'Entrega',
        title: 'Forward Deployed Engineering',
        intro: 'Engenheiros seniores embarcam na sua operação e levam o problema do discovery à produção. Pods híbridos agênticos: um a três seniores dirigindo um time de agentes, com governança de ponta a ponta.',
        items: [
          {
            name: 'Pod Híbrido Agêntico',
            body: 'O pod é dono do resultado: aprende o negócio, desenha a solução, entrega em produção e permanece responsável até o go-live.',
            duration: 'Engajamentos a partir de 6 semanas',
            leave: 'Software funcionando em produção e visibilidade total da entrega',
            next: 'Handover para o seu time, ou retainer',
          },
          {
            name: 'Handover',
            body: 'Seu time herda o sistema com os padrões, o vault e o conhecimento operacional, verificados em sessões de trabalho e não apenas em documentos.',
            duration: '2 a 4 semanas',
            leave: 'Um time dono do que foi construído',
            next: 'Cadência opcional de advisory',
          },
        ],
        cta: 'Veja como o FDE funciona',
      },
      {
        id: 'recurring',
        accent: 19,
        kicker: 'Recorrentes',
        title: 'Afiado entre um engajamento e outro',
        intro: 'Ofertas de continuidade para líderes que querem o olhar externo permanente sobre o sistema.',
        items: [
          {
            name: 'Advisory Executivo',
            body: 'Uma sessão mensal fixa com seu time executivo sobre estratégia, tecnologia e organização.',
            duration: 'Mensal',
            leave: 'Decisões testadas antes de ficarem caras',
            next: '',
          },
          {
            name: 'Conselho Consultivo',
            body: 'Atuação certificada em conselho para questões de tecnologia e transformação.',
            duration: 'Por ciclo',
            leave: 'Uma voz de tecnologia na sala do conselho',
            next: '',
          },
          {
            name: 'Retainer FDE',
            body: 'O pod que construiu seu sistema permanece disponível para evolução e suporte.',
            duration: 'Mensal',
            leave: 'Capacidade de produção que continua melhorando',
            next: '',
          },
        ],
      },
    ],
    footerCta: {
      title: 'Sem certeza de qual encaixa?',
      body: 'Descreva onde dói e respondemos com a forma que propomos, normalmente em um dia útil.',
      cta: 'Fale com a gente',
    },
  },

  es: {
    seo: {
      title: 'Servicios | Lean it 101',
      description:
        'Estrategia, transformación, entrega de software e ingeniería con IA entregadas por practicantes seniores: del workshop ejecutivo a los pods de forward deployed engineering.',
    },
    hero: {
      kicker: 'El catálogo',
      headline: 'Sabes exactamente qué estás contratando.',
      body: 'Trece servicios en seis líneas de trabajo. Cada uno indica su duración, su entregable y su siguiente paso. Elige dónde duele y lee la ficha.',
      cta: 'Empezar una conversación',
    },
    proof: {
      numbers: [
        { value: 'R$50M+', label: 'en ahorro anual documentado' },
        { value: '80%', label: 'de reducción en tickets de soporte' },
        { value: '100+', label: 'iniciativas de transformación' },
        { value: '3.000+', label: 'personas formadas' },
      ],
      names: 'Entregado dentro de Vale, Softplan, Ambev, Segware y Porter Group.',
    },
    meta: { duration: 'Duración', leave: 'Te llevas', next: 'Suele seguir', phases: 'Las fases', talk: 'Abrir una tarjeta sobre esto' },
    groups: [
      {
        id: 'strategy',
        accent: 19,
        kicker: 'Estrategia',
        cta: 'Ver la oferta completa de planificación estratégica',
        ctaHref: '/services/strategic-planning',
        title: 'Estrategia que llega al lunes',
        intro: 'Hoshin Kanri aplicado en serio: los objetivos se vuelven indicadores, los indicadores iniciativas, y toda la empresa ve la conexión.',
        items: [
          {
            name: 'Planificación Estratégica y Despliegue',
            body: 'Un programa bajo Hoshin Kanri. El liderazgo define la dirección en un workshop ejecutivo, los objetivos recorren la organización en rondas de catchball hasta que cada área ha negociado sus metas, a la vista, y una mentoría mensual mantiene viva la estrategia durante el año.',
            duration: '8 semanas, luego ritmo de 12 meses',
            leave: 'X-Matrix, strategy scorecard, playbook de despliegue y un liderazgo que conduce sus propios reviews',
            next: 'El próximo ciclo anual, conducido por tu equipo',
            phases: [
              { name: 'Workshop Ejecutivo de Estrategia', duration: '2 días' },
              { name: 'Catchball y Despliegue', duration: 'Unas 8 semanas' },
              { name: 'Mentoría de Ejecución Estratégica', duration: '12 meses' },
            ],
          },
        ],
      },
      {
        id: 'workshops',
        accent: 10,
        kicker: 'Facilitación',
        title: 'Workshops que terminan en decisión',
        intro: 'Salas enfocadas con la gente correcta, facilitadas hasta un artefacto concreto en lugar de una foto de post-its.',
        items: [
          {
            name: 'Value Stream Mapping',
            body: 'El flujo completo del pedido a la entrega en una pared, con esperas, reprocesos y handoffs valorados en días.',
            duration: '2 a 3 días',
            leave: 'Mapas de estado actual y futuro con backlog de mejoras',
            next: 'Un sprint de rediseño o un compromiso FDE',
          },
          {
            name: 'Rediseño de Producto y Proceso',
            body: 'El formato Porter: mapear, identificar pérdidas, definir el nuevo flujo y acordar quién hace qué entre áreas.',
            duration: '1 a 2 semanas en sesiones',
            leave: 'El nuevo flujo, documentado y acordado por quienes lo operan',
            next: 'Apoyo a la implementación con gestión visual',
          },
          {
            name: 'Fundamentos Lean',
            body: 'Tus líderes aprenden la base sobre la que se sostiene la Pirámide Lean: valor, flujo, pull, desperdicio y mejora como trabajo diario.',
            duration: '2 días',
            leave: 'Un lenguaje común para la transformación que viene',
            next: 'Transformación de la entrega de software con IA',
          },
          {
            name: 'Diseño de Negocio con IA',
            body: 'Dónde la IA cambia tu operación de verdad y dónde solo agrega costo, decidido sobre tus procesos y tus números.',
            duration: '2 días',
            leave: 'Un mapa priorizado de oportunidades de IA, cada una con esfuerzo en rangos',
            next: 'Discovery y assessment técnico de IA',
          },
        ],
      },
      {
        id: 'sdlc',
        accent: 1,
        kicker: 'Entrega de software e IA',
        title: 'Entrega de software reconstruida para la era de los agentes',
        intro: 'La Lean Pyramid 6.0 aplicada a cómo construyen tus equipos: humanos dueños de la estrategia y del oficio, agentes haciendo el trabajo pesado, y todo el flujo visible.',
        items: [
          {
            name: 'Transformación de la Entrega de Software con IA',
            body: 'Una sola transformación de tu ciclo de desarrollo de software (SDLC), conducida dentro de tu flujo de entrega. Tu liderazgo de ingeniería escribe el modelo operativo, los agentes entran a tus repositorios con gobernanza, ingenieros seniors reestructuran la base donde corre el nuevo flujo, y todo se consolida en un playbook vivo con un ritmo mensual de coaching mientras los nuevos hábitos se afirman.',
            duration: 'En general de 8 a 12 semanas, dimensionado por sistema, luego ritmo mensual',
            leave: 'Agentes produciendo trabajo verificado en tus repositorios, el playbook versionado con tu código, líderes que corrigen el sistema',
            next: 'Un pod de FDE, o advisory ejecutivo cuando el alcance crece',
            phases: [
              { name: 'Workshop de Fundamentos', duration: '2 días' },
              { name: 'Orquestación de Agentes', duration: '2 a 4 semanas' },
              { name: 'Arquitectura e Ingeniería', duration: 'Por sistema' },
              { name: 'Playbook de Entrega con IA', duration: '1 a 2 semanas' },
              { name: 'Coaching de Liderazgo', duration: 'Mensual' },
            ],
          },
        ],
      },
      {
        id: 'discovery',
        accent: 10,
        kicker: 'Discovery y assessment',
        title: 'Saber antes de construir',
        intro: 'Compromisos cortos y afilados que convierten una sospecha vaga en una decisión con números.',
        items: [
          {
            name: 'Assessment de Sistemas Legados',
            body: 'Cuánto cuesta de verdad ese sistema viejo, qué costaría modernizarlo y en qué orden sale el riesgo.',
            duration: '2 a 3 semanas',
            leave: 'Un mapa de modernización con esfuerzo, riesgo y secuencia',
            next: 'Un pod FDE para ejecutarlo',
          },
          {
            name: 'Discovery de Mejora de Procesos con IA',
            body: 'Nos sentamos dentro de tu operación, encontramos los procesos donde la IA se paga sola y probamos el mejor con una sonda funcionando.',
            duration: '2 a 4 semanas',
            leave: 'Una lista priorizada de oportunidades y una sonda corriendo en trabajo real',
            next: 'Entrega, con tu equipo o el nuestro',
          },
        ],
      },
      {
        id: 'delivery',
        accent: 1,
        kicker: 'Entrega',
        title: 'Forward Deployed Engineering',
        intro: 'Ingenieros seniores se integran a tu operación y llevan el problema del discovery a producción. Pods híbridos agénticos: de uno a tres seniores dirigiendo un equipo de agentes, gobernado de punta a punta.',
        items: [
          {
            name: 'Pod Híbrido Agêntico',
            body: 'El pod es dueño del resultado: aprende el negocio, diseña la solución, entrega en producción y sigue siendo responsable hasta el go-live.',
            duration: 'Compromisos desde 6 semanas',
            leave: 'Software funcionando en producción y visibilidad total de la entrega',
            next: 'Handover a tu equipo, o retainer',
          },
          {
            name: 'Handover',
            body: 'Tu equipo hereda el sistema con los estándares, el vault y el conocimiento operativo, verificados en sesiones de trabajo y no solo en documentos.',
            duration: '2 a 4 semanas',
            leave: 'Un equipo dueño de lo construido',
            next: 'Cadencia opcional de advisory',
          },
        ],
        cta: 'Ve cómo funciona el FDE',
      },
      {
        id: 'recurring',
        accent: 19,
        kicker: 'Recurrentes',
        title: 'Afilado entre compromisos',
        intro: 'Ofertas de continuidad para líderes que quieren la mirada externa permanente sobre el sistema.',
        items: [
          {
            name: 'Advisory Ejecutivo',
            body: 'Una sesión mensual fija con tu equipo ejecutivo sobre estrategia, tecnología y organización.',
            duration: 'Mensual',
            leave: 'Decisiones probadas antes de que se vuelvan caras',
            next: '',
          },
          {
            name: 'Consejo Consultivo',
            body: 'Actuación certificada en consejos para cuestiones de tecnología y transformación.',
            duration: 'Por ciclo',
            leave: 'Una voz de tecnología en la sala del consejo',
            next: '',
          },
          {
            name: 'Retainer FDE',
            body: 'El pod que construyó tu sistema queda disponible para evolución y soporte.',
            duration: 'Mensual',
            leave: 'Capacidad de producción que sigue mejorando',
            next: '',
          },
        ],
      },
    ],
    footerCta: {
      title: '¿No sabes cuál encaja?',
      body: 'Describe dónde duele y respondemos con la forma que propondríamos, normalmente en un día hábil.',
      cta: 'Habla con nosotros',
    },
  },
}

const normalize = (language) => {
  if (!language) return 'en-US'
  const lower = language.toLowerCase()
  if (lower.startsWith('pt')) return 'pt-BR'
  if (lower.startsWith('es')) return 'es'
  return 'en-US'
}

export const getServicesCatalogContent = (language) =>
  servicesCatalogContent[normalize(language)]

export default servicesCatalogContent
